import { useEffect, useRef } from 'react';
import { CampaignStatus } from './types';

const POLL_INTERVAL_MS = 20000;
const MAX_POLL_ATTEMPTS = 30;

/**
 * Polls the campaign and conversations as a fallback when the AppSync
 * WebSocket subscription silently disconnects or when the backend finishes
 * processing before the page loads (race condition).
 *
 * Polling is active while:
 * - The campaign status is GENERATING_MESSAGES or PROCESSING, OR
 * - The campaign is ready (DRAFT/FINISHED_GENERATING) but conversations
 *   haven't been created yet (totalConversationsCount is 0 despite having
 *   prospects)
 *
 * Stops automatically once data is loaded or after MAX_POLL_ATTEMPTS.
 */
export const useCampaignStatusPolling = ({
  campaignStatus,
  totalConversationsCount,
  totalProspectsCount,
  refetchCampaign,
  refetchConversations
}: {
  campaignStatus: CampaignStatus | undefined;
  totalConversationsCount: number;
  totalProspectsCount: number;
  refetchCampaign: () => void | Promise<void>;
  refetchConversations: () => void | Promise<void>;
}) => {
  const refetchCampaignRef = useRef(refetchCampaign);
  const refetchConversationsRef = useRef(refetchConversations);
  useEffect(() => {
    refetchCampaignRef.current = refetchCampaign;
    refetchConversationsRef.current = refetchConversations;
  });

  const attemptsRef = useRef(0);

  useEffect(() => {
    const isStillProcessing =
      campaignStatus === CampaignStatus.GENERATING_MESSAGES ||
      campaignStatus === CampaignStatus.PROCESSING;

    const isReadyButConversationsMissing =
      (campaignStatus === CampaignStatus.DRAFT ||
        campaignStatus === CampaignStatus.FINISHED_GENERATING) &&
      totalConversationsCount === 0 &&
      totalProspectsCount > 0;

    if (!isStillProcessing && !isReadyButConversationsMissing) return;

    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) return;

    // Fetch immediately on first tick, then every POLL_INTERVAL_MS
    refetchCampaignRef.current();
    refetchConversationsRef.current();
    attemptsRef.current += 1;

    const interval = setInterval(() => {
      attemptsRef.current += 1;
      refetchCampaignRef.current();
      refetchConversationsRef.current();
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [campaignStatus, totalConversationsCount, totalProspectsCount]);
};
